import type { EntranceId, MapId, RoomId } from '../types.ts';
import { BALANCE } from '../balance.ts';
import { MAPS } from './data.ts';
import type { EdgeDef, MapDef, MapShape, RoomDef } from './types.ts';

export function getMap(id: MapId): MapDef {
  return MAPS[id];
}

export function roomDef(map: MapDef, id: RoomId): RoomDef {
  const r = map.rooms.find((x) => x.id === id);
  if (!r) throw new Error(`Unknown room ${id} in map ${map.id}`);
  return r;
}

export function hasRoom(map: MapShape, id: RoomId): boolean {
  return map.rooms.some((r) => r.id === id);
}

/** The edge joining two rooms, in either direction. */
export function edgeBetween(map: MapShape, a: RoomId, b: RoomId): EdgeDef | undefined {
  return map.edges.find((e) => (e.a === a && e.b === b) || (e.a === b && e.b === a));
}

export function isRopeLink(map: MapShape, a: RoomId, b: RoomId): boolean {
  return map.ropeLinks.some(([x, y]) => (x === a && y === b) || (x === b && y === a));
}

export function isOptional(map: MapDef, id: RoomId): boolean {
  return roomDef(map, id).kind === 'optional';
}

export function neighbours(map: MapShape, id: RoomId): RoomId[] {
  const out: RoomId[] = [];
  for (const e of map.edges) {
    if (e.a === id) out.push(e.b);
    else if (e.b === id) out.push(e.a);
  }
  return out;
}

export function ropeNeighbours(map: MapShape, id: RoomId): RoomId[] {
  const out: RoomId[] = [];
  for (const [a, b] of map.ropeLinks) {
    if (a === id) out.push(b);
    else if (b === id) out.push(a);
  }
  return out;
}

/** Core rooms: everything except the dead-end alcoves. */
export function coreRooms<M extends MapShape>(map: M): M['rooms'] {
  return map.rooms.filter((r) => r.kind !== 'optional');
}

export interface RouteOptions {
  /** Allow one rope hop between the rope-linked rooms. */
  allowRope: boolean;
}

/**
 * Every simple route from the entrance room to the vault through core rooms, with
 * `routeMinRooms`–`routeMaxRooms` rooms (GDD §5.2). With `allowRope`, rope links count as
 * adjacency, at most once per route.
 */
export function findRoutes(map: MapShape, entrance: EntranceId, opts: RouteOptions): RoomId[][] {
  const start = map.entrances[entrance];
  const out: RoomId[][] = [];
  const core = new Set(coreRooms(map).map((r) => r.id));

  const walk = (path: RoomId[], ropeUsed: boolean): void => {
    const last = path[path.length - 1] as RoomId;
    if (last === map.vault) {
      if (path.length >= BALANCE.routeMinRooms) out.push(path.slice());
      return;
    }
    if (path.length >= BALANCE.routeMaxRooms) return;
    for (const n of neighbours(map, last)) {
      if (core.has(n) && !path.includes(n)) walk([...path, n], ropeUsed);
    }
    if (opts.allowRope && !ropeUsed) {
      for (const n of ropeNeighbours(map, last)) {
        if (core.has(n) && !path.includes(n)) walk([...path, n], true);
      }
    }
  };
  walk([start], false);
  return out;
}

/** Number of guarded corridors a route crosses (ropes and free passages have none). */
export function guardedHops(map: MapShape, route: readonly RoomId[]): number {
  let n = 0;
  for (let i = 0; i + 1 < route.length; i++) {
    const a = route[i] as RoomId;
    const b = route[i + 1] as RoomId;
    const e = edgeBetween(map, a, b);
    if (e?.guarded) n++;
  }
  return n;
}

/** Problems with a map definition; an empty list means it satisfies GDD §10. */
export function validateMap(map: MapDef): string[] {
  const problems: string[] = [];
  const ids = new Set(map.rooms.map((r) => r.id));
  if (ids.size !== map.rooms.length) problems.push('duplicate room ids');
  if (map.rooms.length < 10 || map.rooms.length > 14) {
    problems.push(`room count ${map.rooms.length} outside 10–14`);
  }
  for (const e of map.edges) {
    if (!ids.has(e.a) || !ids.has(e.b)) problems.push(`edge ${e.a}-${e.b} names an unknown room`);
  }
  for (const [a, b] of map.ropeLinks) {
    if (!ids.has(a) || !ids.has(b)) {
      problems.push(`rope ${a}-${b} names an unknown room`);
      continue;
    }
    if (roomDef(map, a).floor === roomDef(map, b).floor) {
      problems.push(`rope ${a}-${b} stays on one floor`);
    }
    if (roomDef(map, a).kind === 'optional' || roomDef(map, b).kind === 'optional') {
      problems.push(`rope ${a}-${b} touches an alcove`);
    }
  }
  const vaults = map.rooms.filter((r) => r.kind === 'vault');
  if (vaults.length !== 1 || vaults[0]?.id !== map.vault) problems.push('exactly one vault room');

  for (const r of map.rooms) {
    if (r.kind === 'optional') {
      if (neighbours(map, r.id).length !== 1) problems.push(`alcove ${r.id} is not a dead end`);
      if (r.extraLoot <= 0) problems.push(`alcove ${r.id} has no extra loot`);
    } else if (r.extraLoot !== 0) {
      problems.push(`${r.id} has extra loot but is not an alcove`);
    }
    if (r.pool.length === 0) problems.push(`${r.id} has an empty puzzle pool`);
  }

  for (const entrance of ['roof', 'sewer', 'service'] as const) {
    const start = map.entrances[entrance];
    if (!ids.has(start)) {
      problems.push(`entrance ${entrance} names an unknown room`);
      continue;
    }
    const routes = findRoutes(map, entrance, { allowRope: false });
    if (routes.length < 2) problems.push(`entrance ${entrance} has ${routes.length} valid routes`);
  }
  return problems;
}
